import React, { useState, useRef, useEffect, useCallback, ChangeEvent } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  RadioGroup,
  Radio,
  Paper,
  Typography,
  Stack,
} from '@mui/material';
import { getSSOValidationError } from '../../helpers/SSOValidation';
import styles from './styles.pcss';

export type FormStateType = {
  provider: string;
  type: string;
  samlIssuer: string;
  statusBeforeDeactivation: string;
  loginRedirectUrl: string;
  entrypoint: string;
  cert: string;
  issuer: string;
  enableProvisioning: boolean;
  idpDisplayName: string;
  enforceRedirect: boolean;
  maxTtl: string;
  disableRequestedAuthnContext: boolean;
  status: string;
};

type ErrorsType = Record<keyof FormStateType, number>;

export interface SSOFormHandle {
  getFormData(): FormStateType;
  validate(): Promise<boolean>;
  validateAndMarkErrors(): boolean;
  canSubmit(): boolean;
  resetForm(): void;
}

type SSOUploadUSBProps = {
  onFormDataChange?: (formData: FormStateType) => void;
  onFormReady?: (handle: SSOFormHandle) => void;
  initialValues?: Partial<FormStateType>;
};

const SSOUploadUSB = ({ onFormDataChange, onFormReady, initialValues }: SSOUploadUSBProps) => {

  const defaultFormState = {
    provider: 'saml',
    type: 'sso',
    samlIssuer: 'travelbank',
    statusBeforeDeactivation: 'enabled',
    loginRedirectUrl: 'https://api.travelbank.com/auth/saml/',
    entrypoint: '',
    cert: '',
    issuer: '',
    enableProvisioning: false,
    idpDisplayName: '',
    enforceRedirect: false,
    maxTtl: '',
    disableRequestedAuthnContext: false,
    status: 'enabled'
  };

  const [dynamicErrorCount, setDynamicErrorCount] = useState(0);
  const [formState, setFormState] = useState(defaultFormState);

  const errors = useRef({
    entrypoint: 0,
    issuer: 0,
    idpDisplayName: 0,
    statusBeforeDeactivation: 0,
    maxTtl: 0,
    cert: 0,
    provider: 0,
    type: 0,
    samlIssuer: 0,
    loginRedirectUrl: 0,
    enableProvisioning: 0,
    enforceRedirect: 0,
    disableRequestedAuthnContext: 0,
    status: 0
  });
  
  const setErrors = (newErrors: Partial<ErrorsType>) => { errors.current = { ...errors.current, ...newErrors }; }
  
  const canSubmit = useCallback(() => {
    return (
      formState.entrypoint.trim().length > 0 &&
      formState.cert.trim().length > 0 &&
      formState.issuer.trim().length > 0 &&
      formState.idpDisplayName.trim().length > 0 &&
      formState.maxTtl.trim().length > 0 &&
      formState.status.trim().length > 0
    );
  }, [formState]);

  const validateAndMarkErrors = useCallback(() => {
    const newErrors: Partial<ErrorsType> = {};
    let hasErrors = false;

    // Validate maxTtl is numeric and within range (60-86400 seconds)
    const ttlNum = parseInt(formState.maxTtl, 10);
    if (!formState.maxTtl.trim() || isNaN(ttlNum) || ttlNum < 60 || ttlNum > 86400) {
      newErrors.maxTtl = 1;
      hasErrors = true;
    }

    try {
      const url = new URL(formState.entrypoint);
      if (url.protocol !== 'https:') { newErrors.entrypoint = 1; hasErrors = true; }
    } catch { newErrors.entrypoint = 1; hasErrors = true; }

    const validationError = getSSOValidationError(
      formState.entrypoint,
      formState.issuer,
      formState.cert
    );

    if (validationError) {
      hasErrors = true;
      if (!formState.entrypoint.trim()) newErrors.entrypoint = 1;
      if (!formState.cert.trim()) newErrors.cert = 1;
      if (!formState.issuer.trim()) newErrors.issuer = 1;
      if (!formState.idpDisplayName.trim()) newErrors.idpDisplayName = 1;
      if (!formState.status.trim()) newErrors.status = 1;
    } else {
      if (formState.entrypoint.trim().length === 0) {
        newErrors.entrypoint = 1;
        hasErrors = true;
      }
      if (formState.cert.trim().length === 0) {
        newErrors.cert = 1;
        hasErrors = true;
      }
      if (formState.issuer.trim().length === 0) {
        newErrors.issuer = 1;
        hasErrors = true;
      }
      if (formState.idpDisplayName.trim().length === 0) {
        newErrors.idpDisplayName = 1;
        hasErrors = true;
      }
      if (formState.status.trim().length === 0) {
        newErrors.status = 1;
        hasErrors = true;
      }
    }

    setErrors(newErrors);
    setDynamicErrorCount(count => Math.abs(count) + 1);
    return !hasErrors;
  }, [formState]);

  const resetForm = useCallback(() => {
    setFormState(defaultFormState);
    setErrors({});
    setDynamicErrorCount(0);
  }, []);

  const getFormData = useCallback(() => formState, [formState]);

  useEffect(() => {
    if (!initialValues) return;
    setFormState(state => ({
      ...state,
      entrypoint:                 initialValues.entrypoint                 ?? state.entrypoint,
      issuer:                     initialValues.issuer                     ?? state.issuer,
      cert:                       initialValues.cert                       ?? state.cert,
      idpDisplayName:             initialValues.idpDisplayName             ?? state.idpDisplayName,
      statusBeforeDeactivation:   initialValues.statusBeforeDeactivation   ?? state.statusBeforeDeactivation,
      loginRedirectUrl:           initialValues.loginRedirectUrl           ?? state.loginRedirectUrl,
      provider:                   initialValues.provider                   ?? state.provider,
      type:                       initialValues.type                       ?? state.type,
      samlIssuer:                 initialValues.samlIssuer                 ?? state.samlIssuer,
      maxTtl:                     initialValues.maxTtl                     ?? state.maxTtl,
      status:                     initialValues.status                     ?? state.status,
      enableProvisioning:         initialValues.enableProvisioning         ?? state.enableProvisioning,
      enforceRedirect:            initialValues.enforceRedirect            ?? state.enforceRedirect,
      disableRequestedAuthnContext: initialValues.disableRequestedAuthnContext ?? state.disableRequestedAuthnContext,
    }));
  }, [initialValues]);

  useEffect(() => {
    const handle: SSOFormHandle = {
      getFormData,
      validate: () => {
        setDynamicErrorCount(count => Math.abs(count) + 1);
        return new Promise((resolve) => {
          setTimeout(() => {
            const hasError = Object.values(errors.current).some(code => code && code !== 0);
            resolve(!hasError);
          }, 0);
        });
      },
      canSubmit,
      validateAndMarkErrors,
      resetForm,
    };
    onFormReady?.(handle);
  }, [onFormReady, getFormData, canSubmit, validateAndMarkErrors, resetForm]);

  const handleStatusUpdate = (field: keyof FormStateType) => ({ inputValue, errorCode }: { inputValue: any; errorCode?: number }) => {
    setFormState(state => ({ ...state, [field]: inputValue }));
    setErrors({[field]: errorCode || 0 });
  };

  const handleInputChange = (field: keyof FormStateType) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFormState(state => ({ ...state, [field]: value }));
    setErrors({[field]: 0 });
  };

  const handleRadioChange = (field: keyof FormStateType) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = field === 'statusBeforeDeactivation' || field === 'loginRedirectUrl' || field === 'status'
      ? e.target.value 
      : e.target.value === 'true';
    setFormState(state => ({ ...state, [field]: value as any }));
    setErrors({[field]: 0 });
  };

  const hasError = (field: keyof FormStateType): boolean => !!(errors.current[field] && errors.current[field] !== 0);

  return (
    <Paper className={styles.formPaper}>
      <Stack className={styles.formStack}>
        {/* Editable fields section */}
        <Box className={styles.gridContainer}>
          {/* Entrypoint */}
          <TextField
            label="Entrypoint"
            name="entrypoint"
            value={formState.entrypoint}
            onChange={handleInputChange('entrypoint')}
            error={hasError('entrypoint')}
            helperText={hasError('entrypoint') ? 'Entrypoint is required.' : 'HTTPS URL only'}
            fullWidth
            variant="outlined"
            required
            inputProps={{ maxLength: 512 }}
          />

          {/* Issuer */}
          <TextField
            label="Issuer"
            name="issuer"
            value={formState.issuer}
            onChange={handleInputChange('issuer')}
            error={hasError('issuer')}
            helperText={hasError('issuer') ? 'Issuer is required.' : 'HTTPS URL only'}
            fullWidth
            variant="outlined"
            required
            inputProps={{ maxLength: 512 }}
          />

          {/* IDP Display Name */}
          <TextField
            label="IDP Display Name"
            name="idpDisplayName"
            value={formState.idpDisplayName}
            onChange={handleInputChange('idpDisplayName')}
            error={hasError('idpDisplayName')}
            helperText={hasError('idpDisplayName') ? 'IDP Display Name is required.' : ''}
            fullWidth
            variant="outlined"
            required
            inputProps={{ maxLength: 256 }}
          />

          {/* Status */}
          <Box>
            <Box className={styles.fieldLabelRequired}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Status:
              </Typography>
              <Typography variant="body2" className={styles.requiredAsterisk}>*</Typography>
            </Box>
            <RadioGroup
              row
              value={formState.status}
              onChange={handleRadioChange('status')}
              className={styles.radioGroup}
            >
              <FormControlLabel
                value="enabled"
                control={<Radio />}
                label="Enabled"
              />
              <FormControlLabel
                value="disabled"
                control={<Radio />}
                label="Disabled"
              />
            </RadioGroup>
          </Box>

          {/* Max TTL */}
          <TextField
            label="Max TTL"
            name="maxTtl"
            value={formState.maxTtl}
            onChange={handleInputChange('maxTtl')}
            error={hasError('maxTtl')}
            helperText={hasError('maxTtl') ? 'Max TTL must be a number between 60 and 86400 seconds.' : 'Session timeout in seconds (60-86400, 1 minute to 24 hours)'}
            fullWidth
            variant="outlined"
            required
            inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
          />

          {/* Certificate */}
          <TextField
            label="Certificate"
            name="cert"
            value={formState.cert}
            onChange={handleInputChange('cert')}
            error={hasError('cert')}
            helperText={hasError('cert') ? 'Certificate is required.' : 'PEM-encoded X.509 certificate (max 10000 chars)'}
            fullWidth
            variant="outlined"
            required
            multiline
            rows={6}
            inputProps={{ maxLength: 10000 }}
            className={styles.certificateField}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                overflow: 'hidden'
              }
            }}
          />
        </Box>

        {/* Uneditable fields section */}
        <Box className={styles.sectionDivider}>
          <Typography variant="h6" className={styles.sectionTitle}>
            Pre-defined Fields
          </Typography>
        </Box>

        <Box className={styles.gridContainer}>
          {/* Provider */}
          <TextField
            label="Provider"
            name="provider"
            value={formState.provider}
            onChange={handleInputChange('provider')}
            error={hasError('provider')}
            helperText={hasError('provider') ? 'Provider is required.' : ''}
            fullWidth
            variant="outlined"
            disabled
            className={hasError('provider') ? styles.disabledTextFieldError : styles.disabledTextField}
          />

          {/* Type */}
          <TextField
            label="Type"
            name="type"
            value={formState.type}
            onChange={handleInputChange('type')}
            error={hasError('type')}
            helperText={hasError('type') ? 'Type is required.' : ''}
            fullWidth
            variant="outlined"
            disabled
            className={hasError('type') ? styles.disabledTextFieldError : styles.disabledTextField}
          />

          {/* SAML Issuer */}
          <TextField
            label="SAML Issuer"
            name="samlIssuer"
            value={formState.samlIssuer}
            onChange={handleInputChange('samlIssuer')}
            error={hasError('samlIssuer')}
            helperText={hasError('samlIssuer') ? 'SAML Issuer is required.' : ''}
            fullWidth
            variant="outlined"
            disabled
            className={hasError('samlIssuer') ? styles.disabledTextFieldError : styles.disabledTextField}
          />

          {/* Status Before Deactivation */}
          <Box>
            <Typography variant="body2" className={styles.fieldLabelRequired}>
              Status Before Deactivation:
            </Typography>
            <RadioGroup
              row
              value={formState.statusBeforeDeactivation}
              onChange={handleRadioChange('statusBeforeDeactivation')}
              className={styles.radioGroup}
            >
              <FormControlLabel
                value="enabled"
                control={<Radio disabled />}
                label="Enabled"
                disabled
              />
              <FormControlLabel
                value="disabled"
                control={<Radio disabled />}
                label="Disabled"
                disabled
              />
            </RadioGroup>
          </Box>
        </Box>

        {/* Login Redirect URL */}
        <Box className={styles.loginRedirectUrl}>
          <TextField
            label="Login Redirect URL"
            name="loginRedirectUrl"
            value={formState.loginRedirectUrl}
            onChange={handleInputChange('loginRedirectUrl')}
            error={hasError('loginRedirectUrl')}
            helperText={hasError('loginRedirectUrl') ? 'Login Redirect URL is required.' : 'Format: https://api.travelbank.com/auth/saml/[integration id]'}
            fullWidth
            variant="outlined"
            disabled
            className={hasError('loginRedirectUrl') ? styles.disabledTextFieldError : styles.disabledTextField}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

SSOUploadUSB.displayName = 'SSOUploadUSB';

export default SSOUploadUSB;
